import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {useParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import {useQuery} from "@tanstack/react-query";
import laneAliasService from "../../../../services/web/lane-alias-service.js";
import adrMonitoringService from "../../../../services/web/adr-monitoring-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import Swal from "sweetalert2";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import AgGridUtils from "../../../../utils/aggrid-utils.js";
import adrCodeService from "../../../../services/web/adr-code-service.js";
import InputSwitch from "../../../../components/input/input-switch.jsx";
import {FaPlus} from "react-icons/fa";
import AdrCodePreview from "../../../../components/adr-code-preview/adr-code-preview.jsx";
import I18nKeys from "../components/i18n-keys.js";

const daysOfTheWeek = [
    {value: "MONDAY", label: "Segunda-feira"},
    {value: "TUESDAY", label: "Terça-feira"},
    {value: "WEDNESDAY", label: "Quarta-feira"},
    {value: "THURSDAY", label: "Quinta-feira"},
    {value: "FRIDAY", label: "Sexta-feira"},
    {value: "SATURDAY", label: "Sábado"},
    {value: "SUNDAY", label: "Domingo"}
]

const dayMap = Object.fromEntries(
    daysOfTheWeek.map(d => [d.value, d])
);

const defaultDTOValues = {
    "daysOfTheWeek": [],
    "lanes": [],
    "timeIni": 0,
    "timeEnd": 23,
    "expiringDate": null,
    "adrIds": [],
    "status": true
}

function AdrMonitoringForm() {

    const params = useParams();
    const rawId = params?.id;
    const navigate = navigateHook();

    const [currentId, setCurrentId] = useState(rawId && rawId !== "null" ? Number(rawId) : null);
    const [filterValues, setFilterValues] = useState(defaultDTOValues);
    const [isSaving, setIsSaving] = useState(false);
    const [laneOptions, setLaneOptions] = useState([])
    const [laneMapOptions, setLaneMapOptions] = useState({})
    const [adrOptions, setAdrOptions] = useState([])
    const [adrMapOptions, setAdrMapOptions] = useState({})
    const gridRef = useRef(null);

    const isEditMode = !!currentId;

    const {data: lanes, isLoading: isLoadingLanes} = useQuery({
        queryFn: () => {
            return laneAliasService.adrLanes()
        },
        queryKey: ["lanes", {}]
    });

    let {data: monitoring, isLoading: isLoadingMonitoring, refetch} = useQuery({
        queryFn: () => {
            return adrMonitoringService.get(currentId)
        },
        queryKey: ["monitoring", {}],
        enabled: !!currentId
    });

    let {data: adrCodes, isLoading: isLoadingAdrCodes} = useQuery({
        queryFn: () => {
            return adrCodeService.list()
        },
        queryKey: ["adrCodes", []],
    });

    useEffect(() => {

        if (monitoring) {

            setFilterValues({
                "adrIds": monitoring?.specialTriggerDetails?.adrData?.map(adrData => {
                    return {
                        value: adrData?.id,
                        label: adrMapOptions[adrData?.id]?.label
                    }
                }) ?? [],
                "daysOfTheWeek": monitoring?.daysOfWeek?.map(day => {
                    return {
                        value: day,
                        label: dayMap[day]?.label
                    }
                }) ?? [],
                "status": monitoring?.status,
                "lanes": monitoring?.lanes.map(lane => {
                    return {
                        value: lane,
                        label: laneMapOptions[lane]?.label
                    }
                }) ?? [],
                "description": monitoring?.specialTriggerDetails?.description ?? "",
                "timeIni": monitoring?.timeIni ?? 0,
                "timeEnd": monitoring?.timeEnd ?? 23,
                "expiringDate": monitoring?.dateExpiredMonitoring != null ? monitoring?.dateExpiredMonitoring : null,
            })

        }

    }, [monitoring, laneMapOptions, adrMapOptions, dayMap]);

    useEffect(() => {
        if (adrCodes) {
            const toSelectAdrCodes = adrCodes?.filter(adrCode => adrCode?.code)?.map((adrCode) => {
                    return {
                        value: adrCode?.id,
                        label: adrCodeService.formatAdrCode(adrCode?.code) + " - " + adrCode?.description
                    }
                }
            )

            const adrCodesMap = Object.fromEntries(
                toSelectAdrCodes.map(d => [d.value, d])
            );

            setAdrOptions(toSelectAdrCodes);
            setAdrMapOptions(adrCodesMap);

        }

    }, [adrCodes]);

    useEffect(() => {
        if (lanes) {
            const toSelectLanes = lanes
                ?.filter(lane => lane?.adr)
                ?.map((lane) => {
                    return {
                        value: lane?.code,
                        label: lane?.formatted_description
                    }
                })

            const laneMap = Object.fromEntries(
                toSelectLanes.map(d => [d.value, d])
            );

            setLaneOptions(toSelectLanes);
            setLaneMapOptions(laneMap);

        }

    }, [lanes]);

    const validate = () => {

        if (filterValues.adrIds.length < 1) {
            return 'O campo "Painéis de segurança" é obrigatório.'
        }
        if (!filterValues.description) {
            return 'O campo "Motivo do monitoramento" é obrigatório.'
        }
        if (filterValues.daysOfTheWeek.length < 1) {
            return 'O campo "Dias da semana" é obrigatório.'
        }
        if (filterValues.lanes.length < 1) {
            return 'O campo "Câmeras" é obrigatório.'
        }
        if (filterValues.timeIni == null) {
            return "O horário inicial é obrigatório"
        }
        if (filterValues.timeEnd == null) {
            return "O horário final é obrigatório"
        }
        if (filterValues?.expiringDate != null && new Date(filterValues?.expiringDate) <= Date.now()) {
            return 'O campo "Data de expiração" não pode ser menor que a data atual.'
        }

        return null;
    };

    const handleSave = async () => {
        if (isSaving) return;

        const err = validate();
        if (err) {
            projectUtils.swalErrorMessage('', err, 'Ok');
            return;
        }

        setIsSaving(true);

        const payload = {
            timeInit: filterValues.timeIni ?? 0,
            timeEnd: filterValues.timeEnd ?? 23,
            outputType: "TRACKING",
            specialTriggerDetails: {
                data: {
                    adrIds: filterValues?.adrIds.map(id => id.value),
                    description: filterValues?.description ?? "",
                },
                type: "ADR"
            },
            status: filterValues?.status ?? true,
            daysOfTheWeek: filterValues.daysOfTheWeek.map(day => day.value),
            lanes: filterValues.lanes.map(lane => lane.value),
            expiringDate: filterValues.expiringDate,
        }

        const request = isEditMode
            ? adrMonitoringService.update(currentId, payload)
            : adrMonitoringService.create(payload);

        try {

            const data = await request;
            const newId = isEditMode ? currentId : (data?.id ?? data);

            projectUtils.success(!isEditMode);

            if (isEditMode) {
                await refetch();
            } else {
                let urlDev = `/adr-monitoring/form/${newId}`;
                navigate(urlDev);
            }

        } catch (error) {

            let message = "Não foi possível salvar.";

            if (error.response?.data && error.response?.data?.messageKey) {
                message = I18nKeys[error.response?.data?.messageKey];
            }

            projectUtils.swalErrorMessage("", message, "Ok");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = () => {
        if (!currentId) return;

        Swal.fire({
            title: "Excluir",
            text: `Excluir o monitoramento?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#063A6C",
            confirmButtonText: "Sim",
            cancelButtonColor: "#c10007",
            cancelButtonText: "Não",
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                return adrMonitoringService
                    .remove(currentId)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        let urlDev = `/adr-monitoring/`;
                        navigate(urlDev, "");
                    })
                    .catch((error) => {
                        console.error(error);
                        let message = "Não foi possível excluir.";

                        if (error.response?.status !== 500) {
                            message = error.response?.data?.message || message;
                        }
                        projectUtils.swalErrorMessage("", message, "Ok");
                    });

            }
        });
    };

    const onChange = ({target}) => {
        let {id, value} = target;
        setFilterValues(prev => ({...prev, [id]: value}));
    }

    const handleSelect = (id, value) => setFilterValues(prev => ({...prev, [id]: value}));

    const columnDefs = useMemo(
        () => [
            {
                headerName: "Câmera",
                field: "label",
                flex: 1,
                comparator: AgGridUtils.nullLastComparator,
            },
        ],
        []
    );

    const adrCodeMap = useMemo(
        () => Object.fromEntries((adrCodes ?? []).map(adr => [adr.id, adr])),
        [adrCodes]
    );

    const adrColumnDefs = useMemo(
        () => [
            {
                headerName: "Painel de segurança",
                width: 160,
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                },
                cellRenderer: (params) => {
                    const adr = adrCodeMap[params.data?.value];
                    return (
                        <AdrCodePreview
                            code={adrCodeService.formatAdrCode(adr?.code)}
                            width={65}
                            showLabel={false}
                        />
                    );
                },

                comparator: AgGridUtils.nullLastComparator,
            },
            {
                headerName: "Nº Risco",
                width: 110,
                valueGetter: (params) => {
                    const formatted = adrCodeService.formatAdrCode(adrCodeMap[params.data?.value]?.code);
                    return formatted.split(/\s+/)[0] ?? '';
                },
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                },
                comparator: AgGridUtils.nullLastComparator,
            },
            {
                headerName: "Nº ONU",
                width: 110,
                valueGetter: (params) => {
                    const formatted = adrCodeService.formatAdrCode(adrCodeMap[params.data?.value]?.code);
                    return formatted.split(/\s+/)[1] ?? '';
                },
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                },
                comparator: AgGridUtils.nullLastComparator,
            },
            {
                headerName: "Motivo do monitoramento",
                flex: 1,
                valueGetter: (params) => adrCodeMap[params.data?.value]?.description ?? '',
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                },
                comparator: AgGridUtils.nullLastComparator,
            },
        ],
        [adrCodeMap]
    );

    const newMonitoring = () => {
        const urlDev = "/adr-monitoring/form";
        navigate(urlDev);
    };

    return (
        <Template isLoading={isLoadingLanes || isLoadingMonitoring || isLoadingAdrCodes || isSaving}>
            <Template.Header>
                <Template.Title browserTitle={'Produtos perigosos - Monitoramento'}>Monitoramento</Template.Title>
                {currentId && (
                    <Button onClick={newMonitoring} target="primary-outline"><FaPlus />Adicionar monitoramento</Button>
                )}
            </Template.Header>
            <Template.Body className="space-y-2">
                <Card>
                    <Card.Header>
                        <Card.Title>Dados</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div className="flex flex-col gap-4">
                            <div className="w-full flex center items-center justify-center gap-4 ">
                                <InputForm
                                    id="description"
                                    maxLength={200}
                                    label="Motivo do monitoramento *"
                                    classNameWrapper="flex-1"
                                    value={filterValues?.description ?? ""}
                                    onChange={onChange}
                                    className={'active'}
                                />
                                <InputSwitch
                                    id='status'
                                    label='Ativo'
                                    checked={filterValues?.status ?? true}
                                    onChange={(checked) => handleSelect("status", checked)}
                                />
                            </div>
                            <div className="flex gap-4 ">
                                <SelectComponent
                                    id="daysOfTheWeek"
                                    classNameWrapper="flex-1"
                                    isMulti={true}
                                    value={filterValues?.daysOfTheWeek ?? []}
                                    options={daysOfTheWeek ?? []}
                                    instanceId="daysOfTheWeek"
                                    label={"Dias da semana *"}
                                    onChange={(value) => handleSelect("daysOfTheWeek", value)}
                                    isClearable={false}
                                />
                                <div className={" flex flex-1"}>
                                    <div className="relative flex-1">
                                        <InputForm
                                            label="Horário inicial *"
                                            type="number"
                                            value={filterValues?.timeIni ?? 0}
                                            classNameWrapper="flex-1"
                                            min={0}
                                            max={23}
                                            className={'active'}
                                            onChange={(e) => {
                                                const val = Math.max(0, Math.min(23, Number(e.target.value)));
                                                onChange({target: {id: 'timeIni', value: val}});
                                            }}
                                        />
                                    </div>
                                    <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">às</div>
                                    <div className="relative flex-1">
                                        <InputForm
                                            label="Horário final *"
                                            type="number"
                                            classNameWrapper="flex-1"
                                            value={filterValues?.timeEnd ?? 23}
                                            min={0}
                                            max={23}
                                            className={'active'}
                                            onChange={(e) => {
                                                const val = Math.max(0, Math.min(23, Number(e.target.value)));
                                                onChange({target: {id: 'timeEnd', value: val}});
                                            }}
                                        />
                                    </div>
                                </div>
                                <InputDateTime
                                    id={"expiringDate"}
                                    label={"Data de expiração"}
                                    selected={filterValues?.expiringDate ?? null}
                                    value={filterValues?.expiringDate}
                                    classNameWrapper="flex-1"
                                    dateFormat="LL"
                                    onChange={(item) => onChange({target: {id: 'expiringDate', value: new Date(item)}})}
                                />

                            </div>

                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Header>
                        <Card.Title>Painéis de segurança</Card.Title>
                    </Card.Header>
                    <Card.Body className="h-full gap-3 flex flex-col">
                        <SelectComponent
                            id="adrIds"
                            classNameWrapper="flex-1"
                            isMulti={true}
                            value={filterValues?.adrIds ?? []}
                            options={adrOptions ?? []}
                            label={"Painéis de segurança *"}
                            onChange={(value) => handleSelect("adrIds", value)}
                            isClearable={false}
                            wrapValues={true}
                        />
                        <section className="h-[calc(100vh-900px)] min-h-[240px] w-full border-t border-[#ededed]">
                            <AgGridReact className="h-full "
                                         ref={gridRef}
                                         rowData={filterValues?.adrIds ?? []}
                                         columnDefs={adrColumnDefs}
                                         animateRows={false}
                                         suppressCellFocus={true}
                                         gridOptions={{localeText: aggridUtils.localization()}}
                                         theme={aggridUtils.generalTheme({rowHeight: 65, headerHeight: 30, nested: true})}
                            />
                        </section>
                    </Card.Body>
                </Card>

                <Card className="h-full w-full">
                    <Card.Header>
                        <Card.Title>Câmeras</Card.Title>
                    </Card.Header>
                    <Card.Body className="h-full gap-3 flex flex-col">
                        <div className="flex">
                            <SelectComponent
                                id="lanes"
                                classNameWrapper="flex-1"
                                isMulti={true}
                                value={filterValues?.lanes ?? []}
                                options={laneOptions ?? []}
                                label={"Câmeras *"}
                                onChange={(value) => handleSelect("lanes", value)}
                                isClearable={false}
                            />
                        </div>
                        <section className="h-[calc(100vh-725px)] min-h-[240px] w-full border-t border-[#ededed]">
                            <AgGridReact className="h-full "
                                         ref={gridRef}
                                         rowData={filterValues?.lanes ?? []}
                                         columnDefs={columnDefs}
                                         animateRows={false}
                                         suppressCellFocus={true}
                                         gridOptions={{localeText: aggridUtils.localization()}}
                                         theme={aggridUtils.generalTheme({rowHeight: 37, headerHeight: 30, nested: true})}
                            />
                        </section>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex gap-2 justify-end w-full">
                                {isEditMode && (
                                    <Button onClick={handleDelete} target="danger" actionFeedbackType='delete'>Excluir</Button>
                                )}
                                <Button onClick={handleSave} target="primary"

                                        disabled={isSaving || isLoadingLanes || isLoadingAdrCodes || isLoadingMonitoring} actionFeedbackType='save'>Salvar</Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

            </Template.Body>
        </Template>
    )
}

export default AdrMonitoringForm;